import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ClassicCanadianDishesInVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "classic-canadian-dishes-to-try-in-vancouver";

  // 2) Access articles from your context
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    "intro": "Introduction",
    "poutine": "Poutine",
    "nanaimo-bars": "Nanaimo Bars",
    "butter-tarts": "Butter Tarts",
    "smoked-salmon": "Smoked Salmon",
    "tourtiere": "Tourtière",
    "local-regulations": "Local Regulations",
    "faq": "FAQ",
    "conclusion": "Conclusion",
    "references": "References"
  };

  // ===================================
  // 4) References Data
  // ===================================
  // (All references are derived from conversation context.)
  const referencesData = [
    {
      url: "https://www.destinationvancouver.com/",
      label: "Destination Vancouver"
    },
    {
      url: "https://www.tourismnanaimo.com/",
      label: "Tourism Nanaimo"
    },
    {
      url: "https://www.hellobc.com/",
      label: "Hello BC"
    }
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // The conversation provided these approximate coordinates for each mentioned location.
  // We’ll match them with specific sections. Each location’s id references a “ref” in the text.
  const locations = [
    {
      id: "la-belle-patate",
      lat: 49.28211,
      lng: -123.13357,
      popupText: "La Belle Patate"
    },
    {
      id: "fritz-european-fry-house",
      lat: 49.27970,
      lng: -123.12750,
      popupText: "Fritz European Fry House"
    },
    {
      id: "mean-poutine",
      lat: 49.28050,
      lng: -123.12100,
      popupText: "Mean Poutine"
    },
    {
      id: "belgian-fries",
      lat: 49.26780,
      lng: -123.06920,
      popupText: "Belgian Fries"
    },
    {
      id: "laurelles-fine-foods",
      lat: 49.27116,
      lng: -123.13378,
      popupText: "Laurelle’s Fine Foods (Granville Island)"
    },
    {
      id: "breka-bakery",
      lat: 49.27880,
      lng: -123.12700,
      popupText: "Breka Bakery & Café (Davie)"
    },
    {
      id: "a-la-mode-pie-cafe",
      lat: 49.27116,
      lng: -123.13378,
      popupText: "À La Mode Pie Café (Granville Island)"
    },
    {
      id: "tartine-bread-pies",
      lat: 49.27550,
      lng: -123.13100,
      popupText: "Tartine Bread & Pies"
    },
    {
      id: "salmon-n-bannock",
      lat: 49.26330,
      lng: -123.13100,
      popupText: "Salmon n’ Bannock Bistro"
    },
    {
      id: "st-lawrence-restaurant",
      lat: 49.28330,
      lng: -123.09800,
      popupText: "St. Lawrence Restaurant"
    }
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Classic Canadian Dishes to Try in Vancouver</title>
        <meta
          name="description"
          content="Discover the must-try Canadian dishes in Vancouver, including poutine, Nanaimo bars, butter tarts, smoked salmon, and tourtière. Explore top local spots for an authentic experience."
        />
        <meta
          name="keywords"
          content="Vancouver, Canadian Dishes, Poutine, Nanaimo bars, Butter tarts, Smoked salmon, Tourtière"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/classic-canadian-dishes-to-try-in-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Classic Canadian Dishes to Try in Vancouver"
        />
        <meta
          property="og:description"
          content="A guide to iconic Canadian foods in Vancouver, from poutine to Nanaimo bars and beyond."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/classic-canadian-dishes-to-try-in-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/classic-canadian-dishes-to-try-in-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Classic Canadian Dishes to Try in Vancouver"
        />
        <meta
          name="twitter:description"
          content="Indulge in iconic Canadian staples such as poutine, Nanaimo bars, and smoked salmon while in Vancouver."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/classic-canadian-dishes-to-try-in-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={ref_data?.image || "https://bcouver.com/assets/images/article-fallback.jpg"}
              alt="Classic Canadian Dishes in Vancouver"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia/>

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver boasts a renowned international culinary scene, but the
              city also offers plenty of <strong>classic Canadian dishes</strong>
              that no visitor or local should miss. In this guide, we’ll explore
              favorites such as <em>poutine</em>, <em>Nanaimo bars</em>,
              <em>butter tarts</em>, <em>smoked salmon</em>, and <em>tourtière</em>,
              complete with insider tips and precise locations. Whether you crave
              savory comfort or a sweet treat, these iconic dishes reflect
              Canadian heritage and flavors you won't soon forget.
            </p>

            {/* SECTION: Poutine */}
            <h2 id="poutine">Poutine – Canada’s Comfort Food Classic</h2>
            <p>
              <strong>Poutine</strong> is arguably Canada's most famous dish—
              a hearty mix of fries, cheese curds, and savory gravy. Originating
              in Quebec, poutine has become Canada’s signature comfort food.
              Vancouver offers a range of options, from authentic classic styles
              to creative spins packed with flavorful toppings.
            </p>
            
            <p
              ref={(el) => setSpotRef("la-belle-patate", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>La Belle Patate</strong>{" "}
              <Marker
                coordY={49.28211}
                coordX={-123.13357}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 1215 Davie St, Vancouver, BC V6E 1N4. A Montreal-inspired spot
              serving over 30 varieties of poutine, all made with squeaky
              authentic cheese curds.
            </p>

            <p
              ref={(el) => setSpotRef("fritz-european-fry-house", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Fritz European Fry House</strong>{" "}
              <Marker
                coordY={49.27970}
                coordX={-123.12750}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 718 Davie St, Vancouver, BC V6Z 1B6. Known for its late-night
              poutine fix and hand-cut fries—perfect for post-party cravings.
            </p>

            <p
              ref={(el) => setSpotRef("mean-poutine", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Mean Poutine</strong>{" "}
              <Marker
                coordY={49.28050}
                coordX={-123.12100}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 718 Nelson St, Vancouver, BC V6Z 1L2. Creative variations like
              the butter chicken or perogy poutine for extra indulgence.
            </p>

            <p
              ref={(el) => setSpotRef("belgian-fries", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Belgian Fries</strong>{" "}
              <Marker
                coordY={49.26780}
                coordX={-123.06920}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 1885 Commercial Dr, Vancouver, BC V5N 4A6. Offers Belgian-style
              twice-fried potatoes and many poutine topping choices, great for
              groups and casual dining.
            </p>

            {/* SECTION: Nanaimo Bars */}
            <h2 id="nanaimo-bars">Nanaimo Bars – A Sweet BC Icon</h2>
            <p>
              The <strong>Nanaimo bar</strong> hails from the city of Nanaimo on
              Vancouver Island. No-bake and supremely sweet, it features a
              graham-coconut-nut base, custard butter icing, and chocolate
              ganache top. It’s a classic that found fame at Expo 86 and remains
              a beloved symbol of BC’s dessert scene.
            </p>

            <p
              ref={(el) => setSpotRef("laurelles-fine-foods", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Laurelle’s Fine Foods</strong>{" "}
              <Marker
                coordY={49.27116}
                coordX={-123.13378}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – 1689 Johnston St (Granville Island). Their Nanaimo bars are
              textbook: sweet, gooey, and big enough to share.
            </p>
            <p
              ref={(el) => setSpotRef("breka-bakery", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Breka Bakery & Café (Davie)</strong>{" "}
              <Marker
                coordY={49.27880}
                coordX={-123.12700}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 855 Davie St, Vancouver, BC V6Z 1B7. Open 24/7, Breka is a go-to
              spot for indulgent treats any time of day, including classic
              Nanaimo bars.
            </p>

            {/* SECTION: Butter Tarts */}
            <h2 id="butter-tarts">Butter Tarts – Quintessential Canadian Pastry</h2>
            <p>
              <strong>Butter tarts</strong> are small pastries filled with a
              buttery, sugary mixture that’s caramel-like in flavor. With or
              without raisins or pecans, they’re a fundamental part of Canadian
              baking heritage.
            </p>

            <p
              ref={(el) => setSpotRef("a-la-mode-pie-cafe", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>À La Mode Pie Café</strong>{" "}
              <Marker
                coordY={49.27116}
                coordX={-123.13378}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 1689 Johnston St (Granville Island). Known for exceptional pies,
              À La Mode also crafts decadent butter tarts with a flaky crust and
              sweet filling.
            </p>
            <p
              ref={(el) => setSpotRef("tartine-bread-pies", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Tartine Bread & Pies</strong>{" "}
              <Marker
                coordY={49.27550}
                coordX={-123.13100}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 770 Beach Ave, Vancouver, BC V6Z 3E2. Offers a variety of
              Canadian treats, including a perfectly balanced butter tart—runny
              but not overly messy.
            </p>

            {/* SECTION: Smoked Salmon */}
            <h2 id="smoked-salmon">Smoked Salmon – Pacific Northwest Staple</h2>
            <p>
              With wild Pacific salmon at the heart of BC’s coastal culture,
              <strong>smoked salmon</strong> is a must-try in Vancouver. From
              cold-smoked lox to sweet hot-smoked “candied salmon,” it’s a
              quintessential local specialty often rooted in Indigenous
              traditions.
            </p>

            <p
              ref={(el) => setSpotRef("salmon-n-bannock", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Salmon n’ Bannock Bistro</strong>{" "}
              <Marker
                coordY={49.26330}
                coordX={-123.13100}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 7-1128 W Broadway, Vancouver, BC V6H 1G5. The only
              Indigenous-owned and operated restaurant in Vancouver, offering
              wild salmon served on traditional bannock, for a truly authentic
              taste of the region.
            </p>
            <p>
              Alternatively, Granville Island’s seafood vendors (like Longliner
              Seafoods) feature candied salmon and vacuum-sealed smoked salmon
              ready to take home. It’s a tasty souvenir or quick snack while
              exploring the market.
            </p>

            {/* SECTION: Tourtière */}
            <h2 id="tourtiere">Tourtière – Hearty French Canadian Meat Pie</h2>
            <p>
              <strong>Tourtière</strong> is a French Canadian meat pie usually
              filled with pork, beef, or veal, spiced with cinnamon and cloves.
              Though popular around the holidays, you can enjoy it year-round in
              Vancouver at select spots.
            </p>

            <p
              ref={(el) => setSpotRef("st-lawrence-restaurant", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>St. Lawrence Restaurant</strong>{" "}
              <Marker
                coordY={49.28330}
                coordX={-123.09800}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – 269 Powell St, Vancouver, BC V6A 1G3. Michelin-starred and
              dedicated to traditional Québécois fare. Tourtière makes an
              occasional appearance, so book early if you want to catch it.
            </p>
            <p>
              <strong>Tartine Bread & Pies</strong> also bakes a comforting,
              classic tourtière by the slice or whole—perfect if you want an
              easy meal on a chilly night.
            </p>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations & Best Practices</h2>
            <p>
              Vancouver dining is straightforward, but here are a few guidelines
              to note:
            </p>
            <ul>
              <li>
                <strong>Food Safety</strong>: Restaurants must follow strict
                health regulations; you can generally eat with confidence.
              </li>
              <li>
                <strong>Smoking & Alcohol</strong>: Smoking is banned indoors
                and near doorways; legal drinking age is 19.
              </li>
              <li>
                <strong>Tipping Etiquette</strong>: It’s customary to tip
                15–20% for good service at sit-down restaurants.
              </li>
              <li>
                <strong>Reservations</strong>: Popular spots like St. Lawrence
                require advanced booking, while casual poutine shops are usually
                walk-in only.
              </li>
              <li>
                <strong>Taxes</strong>: Listed menu prices don’t include the
                12% combined GST/PST in BC.
              </li>
            </ul>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Where can I find the best poutine in Vancouver?</h3>
              <p>
                It depends on your preference! For a classic Quebec-style poutine,
                head to <strong>La Belle Patate</strong>. For late-night cravings,
                <strong>Fritz European Fry House</strong> is a local favorite,
                and <strong>Mean Poutine</strong> offers creative toppings.
              </p>

              <h3>2. What is a Nanaimo bar?</h3>
              <p>
                A three-layer, no-bake dessert from BC: a crumbly base
                (chocolate, coconut), a creamy custard icing in the middle, and
                chocolate ganache on top. You can find them at local bakeries
                like <strong>Laurelle’s Fine Foods</strong> and <strong>Breka</strong>.
              </p>

              <h3>3. Is there a vegetarian version of these dishes?</h3>
              <p>
                Absolutely. Many poutineries offer vegetarian gravy, and
                dessert bars (Nanaimo, butter tarts) are typically meat-free.
                For smoked salmon or tourtière, look for veggie substitutions
                at certain specialty shops.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              From gooey cheese-curd poutine to rich, sweet Nanaimo bars,
              Vancouver’s classic Canadian dishes are both comforting and
              culturally significant. Indulge in these local specialties to
              connect with Canada’s culinary heritage—and don’t forget to bring
              your appetite! Whether you’re enjoying a late-night poutine run or
              sampling exquisite tourtière in a Michelin-starred bistro, you’ll
              walk away with a deeper appreciation for the hearty and sweet side
              of Canadian cuisine.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom
              referencesData={referencesData}
              ref_data={ref_data}
            />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}